import React, { useCallback, useRef } from 'react';

import UIRadioBtn from 'Components/UIComponents/UIRadioBtn';
import { TooltipBlock } from 'Components/Tooltip';
import { ContentPosition } from 'Components/Tooltip/TooltipBlock';
import { SignAction, SignActionLabel } from 'Interfaces/Document';

interface SelectActionFormOptionsProps {
  selectedAction: SignAction;
  isChooseActionDisabled?: boolean;
  showFormTitle?: boolean;
  onSelect: (action: SignAction) => void;
}

function getSignActionLabel(action: SignAction) {
  switch (action) {
    case SignAction.SignDocument:
      return SignActionLabel.SIGN_DOCUMENT;
    case SignAction.SignAndSend:
      return SignActionLabel.SIGN_AND_SEND;
    case SignAction.Send:
      return SignActionLabel.SEND;
  }
}

function getActionLabelTooltipText(label: SignActionLabel) {
  switch (label) {
    case SignActionLabel.SIGN_DOCUMENT:
      return 'Sign a document yourself';
    case SignActionLabel.SIGN_AND_SEND:
      return 'Sign a document and send to others to sign';
    case SignActionLabel.SEND:
      return 'Send a document for others to sign';
  }
}

const SelectActionFormOptions = ({
  selectedAction,
  isChooseActionDisabled,
  showFormTitle = true,
  onSelect,
}: SelectActionFormOptionsProps) => {
  const tooltipRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = useCallback(event => {
    if (!tooltipRef.current) return;

    const { currentTarget } = event;

    const parent = document.getElementsByClassName('interactModal__fieldBar');

    if (!parent || !parent[0]) return;

    const target =
      currentTarget.tagName === 'DIV' &&
      currentTarget.classList.contains('radio-button__wrapper')
        ? currentTarget.getElementsByClassName('radio-button__label')?.[0]
        : currentTarget.tagName === 'LABEL' &&
          currentTarget.classList.contains('radio-button__label')
        ? currentTarget
        : undefined;

    if (!target) return;

    tooltipRef.current.innerText = getActionLabelTooltipText(target.innerText);
    tooltipRef.current.style.display = 'initial';
    tooltipRef.current.style.top = `${target.offsetTop - parent[0].scrollTop - 15}px`;
    tooltipRef.current.style.left = `${target.offsetLeft + target.clientWidth}px`;
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (!tooltipRef.current) return;
    tooltipRef.current.style.display = 'none';
  }, []);

  return (
    <div className="selectActionForm__inner">
      {showFormTitle && <p className="selectActionForm__title">Choose an action</p>}
      <div className="selectActionForm__radioGroup">
        {Object.values(SignAction).map(action => (
          <UIRadioBtn
            key={action}
            label={getSignActionLabel(action)}
            handleCheck={() => onSelect(action)}
            isChecked={selectedAction === action}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            isDisabled={isChooseActionDisabled}
          />
        ))}
        <TooltipBlock contentPosition={ContentPosition.RIGHT} ref={tooltipRef}>
          Send a document for others to sign
        </TooltipBlock>
      </div>
    </div>
  );
};

export default SelectActionFormOptions;
